import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Disc3, Headphones, Radio, ArrowRight, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// R15-4 — Trending Now: top 3 public rooms by `participant_count` desc.
// Hidden when all 0. Polls every 30s. The card links into `/r/{room_id}`
// — the existing room flow handles auth + Pre-Room Preview from there.
function TrendingNow() {
  const [rooms, setRooms] = useState([]);
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await fetch(`${API}/api/rooms/public`);
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        const list = Array.isArray(j?.rooms) ? j.rooms : [];
        const sorted = [...list].sort(
          (a, b) => (b.participant_count || 0) - (a.participant_count || 0)
        );
        setRooms(sorted.slice(0, 3));
      } catch { /* */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  const anyLive = rooms.some((r) => (r.participant_count || 0) > 0);
  if (!anyLive) return null;
  return (
    <section
      className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2 mb-14"
      data-testid="landing-trending"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FF007F] animate-pulse" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-white/55 font-semibold">
            Trending now
          </span>
        </div>
        <Link
          to="/rooms"
          className="text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-white/80"
        >
          See all rooms →
        </Link>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        {rooms.map((r) => (
          <Link
            key={r.room_id}
            to={`/r/${encodeURIComponent(r.room_id)}`}
            className="sr-glass rounded-xl p-4 border border-white/10 hover:border-[#00F0FF]/60 transition flex items-center justify-between gap-3"
            data-testid={`landing-trending-room-${r.slug || r.room_id}`}
          >
            <div className="min-w-0">
              <div className="font-display text-sm font-bold truncate">{r.name}</div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[#00F0FF] inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FF007F] animate-pulse" />
                <Users className="w-3 h-3" />
                {r.participant_count || 0} in room
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-white/40 shrink-0" />
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen sr-aurora sr-grain relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage: "url(https://images.pexels.com/photos/10881631/pexels-photo-10881631.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          maskImage: "linear-gradient(180deg, rgba(0,0,0,0.7), rgba(0,0,0,0.2) 60%, transparent)",
          WebkitMaskImage: "linear-gradient(180deg, rgba(0,0,0,0.7), rgba(0,0,0,0.2) 60%, transparent)",
        }}
      />
      <div className="absolute inset-0 bg-[#050508]/70 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <header className="flex items-center justify-between mb-16">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center shadow-[0_0_24px_rgba(255,0,127,0.35)]">
              <Disc3 className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display text-lg font-bold tracking-tight">Full Spectrum Center</span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">Phase 1 · Auth + Rooms</span>
            </div>
          </div>
          <Link to="/login" className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white" data-testid="header-login-link">
            Log in
          </Link>
        </header>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
          <div className="sr-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] uppercase tracking-[0.2em] text-white/60 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF007F] animate-pulse" />
              Live audio · multi-cam
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tighter">
              The <span className="sr-gradient-text">listening room</span>
              <br />for night-owls & DJs.
            </h1>
            <p className="mt-6 text-white/70 text-base sm:text-lg leading-relaxed max-w-xl">
              One DJ on the decks, everyone else on cam. No mic chaos, no talking over the music — just the track and 500 faces in the dark.
            </p>
            <div className="mt-10 flex items-center gap-6 text-white/40 text-xs uppercase tracking-[0.2em]">
              <div className="flex items-center gap-2"><Radio className="w-3.5 h-3.5" /> One-way audio</div>
              <div className="flex items-center gap-2"><Headphones className="w-3.5 h-3.5" /> 500 cams</div>
            </div>
          </div>

          <div className="sr-glass rounded-2xl p-6 sm:p-8 shadow-[0_20px_80px_-20px_rgba(255,0,127,0.25)] sr-fade-up">
            <div className="text-xs uppercase tracking-[0.25em] text-white/40 mb-2">Start here</div>
            <h2 className="font-display text-2xl font-bold mb-2">Make an account.</h2>
            <p className="text-sm text-white/60 mb-6">Free, fast, and required so we know who's hosting.</p>

            <div className="space-y-3">
              <Link
                to="/signup"
                className="group w-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-bold rounded-lg px-6 py-4 hover:opacity-90 transition-all flex items-center justify-between shadow-[0_8px_30px_-8px_rgba(255,0,127,0.6)]"
                data-testid="landing-signup-btn"
              >
                <span className="flex items-center gap-2"><Disc3 className="w-5 h-5" />Sign up</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <div className="relative my-4 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] text-white/30">
                <div className="flex-1 h-px bg-white/10" />
                <span>already have one?</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <Link
                to="/login"
                className="group w-full bg-white/5 border border-white/10 text-white font-semibold rounded-lg px-6 py-4 hover:bg-white/10 hover:border-white/20 transition-all flex items-center justify-between"
                data-testid="landing-login-btn"
              >
                <span className="flex items-center gap-2"><Headphones className="w-5 h-5 text-[#00F0FF]" />Log in</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>

        <footer className="mt-20 pt-8 border-t border-white/5 text-[11px] uppercase tracking-[0.25em] text-white/30 flex flex-wrap items-center justify-between gap-3">
          <span>Full Spectrum Center · v1.0</span>
          <div className="flex items-center gap-4">
            <a href="/keyholder" className="hover:text-white/55 transition" data-testid="footer-keyholder-link">Keyholder Brief</a>
            <a href="/rooms" className="hover:text-white/55 transition">Community Rooms</a>
            <a href="/leaderboard" className="hover:text-amber-300/85 transition" data-testid="footer-leaderboard-link">Leaderboard</a>
            <span className="opacity-60">web · electron later · expo later</span>
          </div>
        </footer>
      </div>
      <TrendingNow />
    </div>
  );
}
